/**
 * Fetches data from the Strapi API.
 *
 * @param {string} path - The API path to fetch (e.g., '/homepage?populate=deep').
 * @param {RequestInit} [options={}] - Optional fetch options (e.g., method, headers, body).
 * @returns {Promise<any>} - A promise that resolves with the JSON response data.
 * @throws {Error} - Throws an error if the fetch fails or the response is not ok.
 */
export async function fetchStrapiAPI<T = any>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const apiUrl = import.meta.env.VITE_STRAPI_API_URL;
  const apiToken = import.meta.env.VITE_STRAPI_API_TOKEN;

  if (!apiUrl) {
    throw new Error(
      "VITE_STRAPI_API_URL is not defined in environment variables.",
    );
  }
  if (!apiToken) {
    // Only throw if the path doesn't look like an auth path
    if (!path.startsWith("/auth")) {
      console.warn("VITE_STRAPI_API_TOKEN is not defined; API calls may fail.");
      // Depending on public API access, you might want to throw an error here
      // throw new Error('VITE_STRAPI_API_TOKEN is not defined.');
    }
  }

  const defaultHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (apiToken) {
    defaultHeaders["Authorization"] = `Bearer ${apiToken}`;
  }

  const mergedOptions: RequestInit = {
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    ...options,
  };

  const requestUrl = `${apiUrl.replace(/\/$/, "")}${path}`; // Ensure no double slash

  try {
    const response = await fetch(requestUrl, mergedOptions);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // Try to get error details
      console.error("Strapi API Error Response:", errorData);
      throw new Error(
        `Strapi API request failed: ${response.status} ${response.statusText} - ${
          JSON.stringify(errorData.error?.message) ||
          "No error details available."
        }`,
      );
    }

    // Handle empty responses (e.g., 204 No Content)
    if (response.status === 204) {
      return null as T;
    }

    const data = await response.json();
    return data as T; // Return the full response, let the caller handle the data structure
  } catch (error) {
    console.error("Strapi API Fetch Error:", error);
    // Re-throw the error for the calling code to handle
    if (error instanceof Error) {
      throw new Error(`Failed to fetch from Strapi API: ${error.message}`);
    } else {
      throw new Error(
        `An unknown error occurred while fetching from Strapi API.`,
      );
    }
  }
}
